"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";

interface Feature15Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  removePaddingBottom?: boolean;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  items?: ItemType[];
  attributes?: React.HTMLProps<HTMLElement>;
}

type ItemType = {
  title?: string;
  content?: string;
  image?: string;
  imageAltText?: string;
  link?: string;
};

const Feature15 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 15",
  removePaddingBottom = false,
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  items = [],
  attributes,
}: Feature15Props) => {

  const itemBackgroundColors = variantBackgroundColors(darkMode, variant);

  const Item = ({
    title,
    content,
    image,
    imageAltText,
    link,
    className,
  }: ItemType & { className?: string }) => {
    return (
      <Card
        className={cn(
          "p-0 border-none justify-between gap-0",
          itemBackgroundColors,
          className,
        )}
      >
        {link ? (
          <Link href={link ?? ""}>
          <CardHeader className="p-6 flex justify-between items-center gap-6">
            <div>
            {title && (
              <h6>
                {parse(title)}
              </h6>
            )}
            {content && (
              <div className="paragraph-base text-muted-foreground">
                {parse(content)}
              </div>
            )}
            </div>
            <Button asChild variant="outline" grayBorder size="iconLg">
              <span className="material-symbols-outlined">
                arrow_forward
              </span>
            </Button>
          </CardHeader>

          <CardContent
            className="relative flex overflow-hidden p-0"
          >
            <Image
              src={image ?? ""}
              alt={imageAltText ?? ""}
              title={imageAltText ?? ""}
              className="flex-1 rounded-b-xl object-cover aspect-[652/488]"
              width={652}
              height={488}
            />
          </CardContent>
          </Link>
        ) : (
          <>  
            <CardHeader className="p-6">
              {title && (
                <h6>
                  {parse(title)}
                </h6>
              )}
              {content && (
                <div className="paragraph-base text-muted-foreground">
                  {parse(content)}
                </div>
              )}
            </CardHeader>

            <CardContent
              className="relative flex overflow-hidden p-0"
            >
              <Image
                src={image ?? ""}
                alt={imageAltText ?? ""}
                title={imageAltText ?? ""}
                className="flex-1 rounded-b-xl object-cover aspect-[652/488]"
                width={652}
                height={488}
              />
            </CardContent>
          </>
        )}
      </Card>
    );
  };
  
  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      removePaddingBottom={removePaddingBottom}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature15"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-6 lg:flex-row" data-aos="fade-up">
          {title && (
            <div className="lg:w-3/5 text-center lg:text-left">
              {badge && (
                <Badge variant="outline" bold className="mb-6">
                  {badge}
                </Badge>
              )}
              <h2>
                {parse(title)}
              </h2>
            </div>
          )}
          {(content || hasButton) && (
            <div className="lg:w-2/5 flex flex-col gap-6">
              {content && (
                <div className="text-muted-foreground text-center lg:text-left">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                  {secondaryButton && (
                    <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {items && items.length > 0 && (
          <div className="mt-16 md:mt-32 grid grid-cols-1 gap-8 md:grid-cols-6" data-aos="fade-up" data-aos-delay={100}>
            {items.slice(0, 3).map((item: ItemType, i: number) => {
              const gridClasses = items.length === 1 
                ? "md:col-span-6"
                : items.length === 2 
                ? {
                    0: "md:col-span-3",
                    1: "md:col-span-3 md:col-start-4",
                  }[i]
                : "md:col-span-2";
              return <Item key={i} {...item} className={gridClasses} />;
            })}
          </div>
        )}
        
      </div>
    </SectionWrapper>
  )
};

export { Feature15 };